
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMainLinks } from '@/hooks/use-main-links';
import { useLinks } from '@/hooks/use-links';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Settings } from 'lucide-react';
import { MainLink } from '@/types/link-short';
import { LinkPreview } from '@/components/link-short/LinkPreview';
import { LinksManager } from '@/components/link-short/LinksManager';
import { AvatarUploader } from '@/components/link-short/AvatarUploader';
import { ColorPalette } from '@/components/link-short/ColorPalette';

export function EditLinkPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mainLinks, updateMainLink } = useMainLinks();
  const [loading, setLoading] = useState(false);
  const [mainLink, setMainLink] = useState<MainLink | null>(null);
  const { links } = useLinks(id || '');
  const [showSettings, setShowSettings] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    is_active: true,
    bg_color: '#3b82f6',
    text_color: '#ffffff',
    avatar_url: ''
  });

  const [previewMainLink, setPreviewMainLink] = useState<MainLink | null>(null);

  useEffect(() => {
    if (id && mainLinks.length > 0) {
      const foundLink = mainLinks.find(link => link.id === id);
      if (foundLink) {
        setMainLink(foundLink);
        const initialData = {
          title: foundLink.title,
          slug: foundLink.slug,
          description: foundLink.description || '',
          is_active: foundLink.is_active,
          bg_color: foundLink.bg_color,
          text_color: foundLink.text_color,
          avatar_url: foundLink.avatar_url || ''
        };
        setFormData(initialData);
        setPreviewMainLink(foundLink);
      }
    }
  }, [id, mainLinks]);

  useEffect(() => {
    if (mainLink) {
      setPreviewMainLink({
        ...mainLink,
        ...formData
      });
    }
  }, [formData, mainLink]);

  // Auto-salvar mudanças de cor
  useEffect(() => {
    if (mainLink && (formData.bg_color !== mainLink.bg_color || formData.text_color !== mainLink.text_color)) {
      const timeoutId = setTimeout(async () => {
        await updateMainLink(mainLink.id, {
          bg_color: formData.bg_color,
          text_color: formData.text_color
        });
      }, 500); // Debounce de 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [formData.bg_color, formData.text_color, mainLink, updateMainLink]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mainLink) return;

    setLoading(true);
    try {
      const success = await updateMainLink(mainLink.id, formData);
      if (success) {
        navigate('/link-short');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (field: 'bg_color' | 'text_color', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!mainLink) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/link-short')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-xl font-semibold">{mainLink.title}</h1>
              <p className="text-sm text-muted-foreground">
                {window.location.host}/{mainLink.slug}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Configurações
            </Button>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
            />
            <span className="text-sm text-muted-foreground">
              {formData.is_active ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Panel - Links Management */}
          <div className="lg:col-span-7">
            <div className="space-y-6">
              {/* Alert Banner */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <p className="text-sm text-blue-900 font-medium">
                      Seu Link está ativo: {window.location.host}/{mainLink.slug}
                    </p>
                    <button 
                      className="text-sm text-blue-700 underline hover:no-underline"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/${mainLink.slug}`);
                      }}
                    >
                      Copiar URL do seu Link
                    </button>
                  </div>
                </div>
              </div>

              {/* Settings Panel */}
              {showSettings && (
                <Card className="shadow-lg border-0 bg-white">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-t-lg">
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5 text-purple-600" />
                      Configurações do Perfil
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="title">Título do Perfil</Label>
                          <Input
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Seu nome ou marca"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="description">Bio</Label>
                          <Input
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Adicione uma bio"
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <AvatarUploader
                          currentAvatar={formData.avatar_url}
                          onAvatarChange={(url) => setFormData(prev => ({ ...prev, avatar_url: url }))}
                          fallbackText={formData.title?.charAt(0) || 'U'}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <ColorPalette
                        label="Cor de Fundo"
                        value={formData.bg_color}
                        onChange={(value) => handleColorChange('bg_color', value)}
                        showGradients={true}
                      />

                      <ColorPalette
                        label="Cor do Texto"
                        value={formData.text_color}
                        onChange={(value) => handleColorChange('text_color', value)}
                      />
                    </div>

                    <Button onClick={handleSubmit} disabled={loading} className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                      {loading ? 'Salvando...' : 'Salvar Configurações'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Links Management */}
              <Card className="shadow-lg border-0 bg-white">
                <CardContent className="p-6">
                  <LinksManager mainLinkId={mainLink.id} />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Panel - Mobile Preview */}
          <div className="lg:col-span-5">
            <div className="sticky top-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Pré-visualização
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Veja como sua página fica no celular
                </p>
              </div>
              
              <div className="flex justify-center">
                <div className="relative">
                  {/* Phone Frame */}
                  <div className="w-80 h-[640px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
                    <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden">
                      {previewMainLink && (
                        <LinkPreview 
                          mainLink={previewMainLink} 
                          links={links}
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Phone Details */}
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-700 rounded-full"></div>
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-700 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
